import { Component, ViewChild } from '@angular/core';
import { DisplayMode } from '../../shared/display-mode';
import { Region } from '../shared/region.model';
import { RegionService } from '../shared/region.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../../shared/services/loader.service';
import { ActivatedRoute } from '@angular/router';
import { finalize, tap } from 'rxjs/operators';
import { Location } from '@angular/common';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { NgForm } from '@angular/forms';
import { ModalService } from '../../shared/services/modal.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { DistanceUnit } from '../../shared/distance-unit';
import { TimezoneService } from '../../shared/services/timezone.service';
import { forkJoin, iif, noop, of } from 'rxjs';
import { Country } from '../shared/country.model';
import { CountryService } from '../shared/country.service';
import { Languages } from '../../core/languages';
import { RegionFeatureOverrideable } from '../shared/region-features.model';
import { FeatureUtils } from '../shared/feature-utils';
import { RegionFeatureStatus } from '../shared/region-feature-status.model';
import { LocalLocationType } from '../../local-loads/shared/local-location-type';
import { BluechatNoteFooterField } from '../shared/bluechat/bluchat-footer-note-fields.model';
import {
    RegionFeatureDisplayMode
} from '../region-features-editor/region-feature-new-editor/region-feature-new.component';
import { OnOffFeature } from '../shared/on-off-feature.model';

@Component({
  selector: 'app-region-form',
  templateUrl: './region-form.component.html',
  styleUrls: ['./region-form.component.scss']
})
export class RegionFormComponent implements ComponentCanDeactivate {
  public readonly noop = noop;

  region: Region;
  mode          = DisplayMode.Creation;
  DisplayMode   = DisplayMode;
  DistanceUnit  = DistanceUnit;
  RegionFeature = RegionFeatureOverrideable;

  formProcessingBlocked = false;
  deletionBlocked = false;

  timezones: string[] = [];
  languages = Languages.values();
  countries: Country[] = [];
  bluechatNoteFooterFields = Object.values(BluechatNoteFooterField);
  sapDocumentTypeUploadToBlueyonder = FeatureUtils.SAP_DOCUMENT_TYPE_UPLOAD_TO_BLUEYONDER;

  errorMessageKey = null;

  activeTab: 'info' | 'locations' | 'carriers' = 'info';

  onConfig = false;

  protected readonly RegionFeatureStatus = RegionFeatureStatus;
  protected readonly LocationType = LocalLocationType;
  protected readonly RegionFeatureDisplayMode = RegionFeatureDisplayMode;
  protected readonly OnOffFeature = OnOffFeature;

  @ViewChild('generalForm', { static: false })
  generalForm: NgForm;

  @ViewChild('locationsForm', { static: false })
  locationsForm: NgForm;

  @ViewChild('carriersForm', { static: false })
  carriersForm: NgForm;

  constructor(
    private regionService: RegionService,
    private timezoneService: TimezoneService,
    private countryService: CountryService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private navigation: NavigationService,
    public location: Location,
    private modal: ModalService,
    private errorHandler: ErrorHandlerService,
  ) {
    this.route.params.subscribe(({ regionId }): void => {

      const getRegionObservable    = iif(
        () => !!regionId,
        this.regionService.getById(regionId)
          .pipe(tap(() => this.switchToUpdateMode())),
        of(Region.withDefaultBufferDurations()),
      );
      const getTimezonesObservable = this.timezoneService.getTimezones();
      const getCountriesObservable = this.countryService.getAll();

      this.blockForm(false);

      forkJoin([getRegionObservable, getTimezonesObservable, getCountriesObservable])
        .pipe(finalize(() => this.unblockForm()))
        .subscribe(([region, timezones, countries]) => {
          this.region    = region;
          this.timezones = timezones;
          this.countries = countries;
        }, e => this.errorHandler.handleHttpError(e, 'regions.form.errors.fetch-region', false));
    });
  }

  getForm() {
    const formList = [this.generalForm, this.locationsForm, this.carriersForm];
    const formChanged = formList.filter(form => !form.pristine);
    if (formChanged.length) {
      return formChanged[0];
    }
    switch (this.activeTab) {
      case 'info':
        return this.generalForm;
      case 'locations':
        return this.locationsForm;
      case 'carriers':
        return this.carriersForm;
    }
  }

  private markFormsAsPristine() {
    this.generalForm.form.markAsPristine();
    this.locationsForm.form.markAsPristine();
    this.carriersForm.form.markAsPristine();
  }

  switchToUpdateMode() {
    this.mode = DisplayMode.Edition;
  }

  processForm(): void {
    if (this.mode === DisplayMode.Edition) {
      this.updateRegion();
    } else {
      this.createRegion();
    }
  }

  saveRegion() {
    if (!this.region.id) {
      this.createRegion();
    } else {
      this.updateRegion();
    }
  }

  private createRegion() {
    this.regionService.create(this.region)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('regions.form.successes.create'));

        this.navigation.regions()
          .list()
          .go();
      }, e => this.errorHandler.handleHttpError(e, 'regions.form.errors.create'));
  }

  private updateRegion() {
    this.blockForm();

    this.regionService.update(this.region)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(region => {
        console.log(region);
        this.markFormsAsPristine();
        this.region = region;

        this.toastr.success(this.translate.instant('regions.form.successes.update'));
      }, e => this.errorHandler.handleHttpError(e, 'regions.form.errors.update'));
  }

  openDeleteConfirm(content) {
    this.modal.open({
      title: this.translate.instant('regions.form.modals.delete.title'),
      content,
      closeButton: {
        text: this.translate.instant('regions.form.modals.delete.buttons.no')
      },
      validateButton: {
        type: 'danger',
        text: this.translate.instant('regions.form.modals.delete.buttons.yes'),
        icon: 'fa fa-trash-alt'
      },
      size: 'sm'
    }).subscribe(() => this.deleteRegion());
  }

  deleteRegion(): void {
    this.blockForm();
    this.deletionBlocked = true;
    this.markFormsAsPristine();

    this.regionService.remove(this.region.id)
      .pipe(finalize(() => this.unblockForm()))
      .subscribe(() => {
        this.toastr.success(this.translate.instant('regions.form.successes.delete'));

        this.navigation.regions()
          .list()
          .go();
      }, e => this.errorHandler.handleHttpError(e, 'regions.form.errors.delete'));
  }

  blockForm(smallLoader = true) {
    this.formProcessingBlocked = true;
    this.loader.startLoad(smallLoader);
  }

  unblockForm() {
    this.loader.stopLoad();
    this.formProcessingBlocked = false;
  }

  public onNumberInputKeydown(event: KeyboardEvent): void {
    const forbiddenKeys = ['e', '-'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  customizeBluechatActivationChanged(value: OnOffFeature) {
    this.region.features.customizeBluechatFooterValue = value;
    if (this.region.features.bluechatNoteFooterFields.length === 0 && value === OnOffFeature.ON) {
      this.region.features.bluechatNoteFooterFields = [... this.bluechatNoteFooterFields];
    }
  }
}
